/*
 * ---------------------------------------------------------------------------
 * COMMERCIAL IN CONFIDENCE
 *
 * (c) Copyright Quosient Ltd. All Rights Reserved.
 *
 * See LICENSE.txt in the repository root.
 * ---------------------------------------------------------------------------
 */

/**
 * This file provides access to Firebase Functions deployed in the cloud, for the frontend UI.
 *
 * The functions are imported into front end code.
 * Any logging performed here will appear in the user's browser's JS console.
 * Note also that therefore we have no control over environment variables.
 */
import { app } from "./firebase.js";

const regionalFunctions = app.functions('europe-west2');

const functions = {
  getAllUsers: regionalFunctions.httpsCallable('getAllUsers'),
  addUser: regionalFunctions.httpsCallable('addUser'),
  updateUser: regionalFunctions.httpsCallable('updateUser'),
  deleteUser: regionalFunctions.httpsCallable('deleteUser'),
  acceptTerms: regionalFunctions.httpsCallable('acceptTerms'),
  checkTaskStatus: regionalFunctions.httpsCallable('checkTaskStatus'),
  updateAnalytics: regionalFunctions.httpsCallable('updateAnalytics'),
  geeUploadStatus: regionalFunctions.httpsCallable('geeUploadStatus'),
  geeUserAssets: regionalFunctions.httpsCallable('geeUserAssets'),
  getTable: regionalFunctions.httpsCallable('getTable'),
  deleteUserAsset: regionalFunctions.httpsCallable('deleteUserAsset'),
}

/**
 * emulator ports lookup table for functions framework
 */
const functionEmulatorPorts = {
  geeUserAssets: 5050,
  deleteUserAsset: 5052,
  getTable: 5051,
}

/**
 * simple HTTP POST request
 * @param {String} url url for request to be made
 * @param {Object} data - object to be sent in request
 * @returns
 */
function postData(url, data) {
  console.log("making a fetch request to", url);
  return fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(data),
  }).then(
    response => {
      // https://github.com/whatwg/fetch/issues/113
      if (response.status == 204) return '';
      return response.json()
    });
}

/**
 * Replaces functions from emulators to point towards fetch requests
 * to functions framework
 */
function setupPythonHTTPSEmulators() {
  functions.geeUserAssets = (data) => {
    let body = { "data": data };
    return postData(`http://localhost:${functionEmulatorPorts.geeUserAssets}/get_user_assets`, body);
  }
  functions.deleteUserAsset = (data) => {
    let body = { "data": data }
    return postData(`http://localhost:${functionEmulatorPorts.deleteUserAsset}/delete_user_asset`, body);
  }
  functions.getTable = (data) => {
    let body = { "data": data }
    return postData(`http://localhost:${functionEmulatorPorts.getTable}/`, body);
  }
}

// Use the functions emulator if in development mode
if (process.env.NODE_ENV === "development" || location.hostname === 'localhost') {
  console.info("Dev environment: Using functions emulator");
  regionalFunctions.useEmulator("localhost", 5001);
  // Python functions emulator
  setupPythonHTTPSEmulators();
}

export {
  functions
}
